// Task Monitoring Dashboard (Phase 3 Enhanced)
// Management and IC Personnel oversight with real-time updates, deviations, and high-risk tracking

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, CheckCircle, Clock, TrendingUp, AlertTriangle, RefreshCw, Activity, MapPin, Shield } from 'lucide-react';
import { useTasks, useTaskStatistics } from '../../hooks/useTasks';
import { taskExecutionService } from '../../services/taskExecutionService';
import { riskCalculationService } from '../../services/riskCalculationService';
import Modal from '../ui/Modal';
import './TaskMonitoringDashboard.css';

const TaskMonitoringDashboard = ({ userId }) => {
  const { t } = useTranslation('tasks');
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    taskType: '',
    departmentId: '',
    searchTerm: ''
  });

  const [executions, setExecutions] = useState([]);
  const [deviations, setDeviations] = useState([]);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);

  const { tasks, loading, error, refresh } = useTasks(filters);
  const { stats, loading: statsLoading } = useTaskStatistics(filters);

  // Phase 3: Auto-refresh every 30 seconds, but only when tab is visible (avoids ERR_NETWORK_IO_SUSPENDED / 400 after background)
  const MAX_CONCURRENT_EXECUTION_FETCHES = 10;
  useEffect(() => {
    if (!autoRefresh) return;

    const doRefresh = () => {
      if (typeof document.hidden !== 'undefined' && document.hidden) return;
      refresh();
      loadExecutions();
      loadDeviations();
      setLastRefresh(Date.now());
    };

    const interval = setInterval(doRefresh, 30000);

    const onVisibilityChange = () => {
      if (!document.hidden) {
        doRefresh();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [autoRefresh, refresh]);

  // Phase 3: Load executions and deviations when tasks change
  useEffect(() => {
    loadExecutions();
    loadDeviations();
  }, [tasks]);

  const loadExecutions = async () => {
    try {
      const activeTasks = tasks.filter(t => t.status === 'in_progress');
      const toFetch = activeTasks.slice(0, MAX_CONCURRENT_EXECUTION_FETCHES);
      const executionPromises = toFetch.map(task =>
        taskExecutionService.getExecutionByTaskId(task.id)
      );
      const executionResults = await Promise.all(executionPromises);
      setExecutions(executionResults.filter(e => e !== null));
    } catch (err) {
      console.error('Error loading executions:', err);
      setExecutions(prev => (prev.length ? prev : []));
    }
  };

  const loadDeviations = async () => {
    try {
      const allDeviations = tasks.flatMap(task => 
        (task.deviations || []).filter(d => !d.resolved)
      );
      setDeviations(allDeviations);
    } catch (err) {
      console.error('Error loading deviations:', err);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return t('notSet');
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  const getTaskStatusLabel = (task) => {
    if (task.status === 'completed' && task.completedWithSkips) return t('completedWithSkipsStatus');
    const map = {
      pending: t('pending'),
      assigned: t('assigned'),
      acknowledged: t('acknowledged'),
      in_progress: t('inProgress'),
      completed: t('completedAt'),
      completed_with_skips: t('completedWithSkipsStatus'),
      cancelled: t('cancelled'),
      overdue: t('overdue'),
    };
    return map[task.status] || task.status;
  };

  const getTaskProgress = (task) => {
    let totalSteps = 0;
    let completedSteps = 0;
    
    (task.protocols || []).forEach(protocol => {
      const steps = protocol.steps || [];
      totalSteps += steps.length;
      completedSteps += steps.filter(s => s.isComplete || s.isSkipped).length;
    });
    
    if (totalSteps === 0) return 0;
    return Math.round((completedSteps / totalSteps) * 100);
  };

  /** Returns { completedSteps, totalSteps } for progress details. */
  const getTaskProgressBreakdown = (task) => {
    let totalSteps = 0;
    let completedSteps = 0;
    (task.protocols || []).forEach(protocol => {
      const steps = protocol.steps || [];
      totalSteps += steps.length;
      completedSteps += steps.filter(s => s.isComplete || s.isSkipped).length;
    });
    return { completedSteps, totalSteps };
  };

  // Phase 3: High-risk tasks in progress
  const highRiskTasks = executions.filter(exec => {
    const maxRPN = exec.risk_data?.protocol_max_rpn || 0;
    return maxRPN >= 500;
  });

  // Phase 3: Active deviations requiring action
  const criticalDeviations = deviations.filter(d => 
    d.severity === 'critical' || d.severity === 'major'
  );

  // Phase 3: Filter tasks
  const filteredTasks = tasks.filter(task => {
    if (filters.searchTerm && !task.name.toLowerCase().includes(filters.searchTerm.toLowerCase())) {
      return false;
    }
    if (filters.status && task.status !== filters.status) {
      return false;
    }
    if (filters.priority && task.priority !== filters.priority) {
      return false;
    }
    return true;
  });

  return (
    <div className="task-monitoring-dashboard">
      <div className="dashboard-header">
        <div>
          <h2>{t('taskMonitoring')}</h2>
          <p className="subtitle">
            {autoRefresh && <><Activity size={14} /> {t('liveUpdates')}</>}
            {!autoRefresh && <>{t('manualRefresh')}</>}
          </p>
        </div>
        <div className="header-actions">
          <button 
            className="btn btn-secondary" 
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            {autoRefresh ? t('pauseAutoRefresh') : t('startAutoRefresh')}
          </button>
          <button className="btn btn-secondary" onClick={() => {
            refresh();
            loadExecutions();
            loadDeviations();
            setLastRefresh(Date.now());
          }}>
            <RefreshCw size={16} /> {t('refresh')}
        </button>
          {lastRefresh && (
            <span className="last-refresh">
              {t('lastRefresh')}: {new Date(lastRefresh).toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      {/* Summary Statistics */}
      {!statsLoading && stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#f5f3ff', color: '#8b5cf6' }}>
              <Clock size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{executions.length || stats.inProgress || 0}</div>
              <div className="stat-label">{t('tasksNow')}</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#f0fdf4', color: '#10b981' }}>
              <CheckCircle size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.completed}</div>
              <div className="stat-label">{t('completed')}</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#fef2f2', color: '#ef4444' }}>
              <AlertCircle size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{criticalDeviations.length}</div>
              <div className="stat-label">{t('activeDeviations')}</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#fff3cd', color: '#f59e0b' }}>
              <AlertTriangle size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{highRiskTasks.length}</div>
              <div className="stat-label">{t('highRiskTasks')}</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#f5f3ff', color: '#8b5cf6' }}>
              <TrendingUp size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.avgComplianceScore}%</div>
              <div className="stat-label">{t('avgCompliance')}</div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="monitoring-filters">
        <input
          type="text"
          placeholder={t('searchTasksPlaceholder')}
          value={filters.searchTerm}
          onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
          className="search-input"
        />
        <select
          value={filters.status}
          onChange={(e) => handleFilterChange('status', e.target.value)}
          className="filter-select"
        >
          <option value="">{t('allStatuses')}</option>
          <option value="assigned">{t('assigned')}</option>
          <option value="in_progress">{t('inProgress')}</option>
          <option value="completed">{t('completed')}</option>
          <option value="overdue">{t('overdue')}</option>
        </select>
        <select
          value={filters.priority}
          onChange={(e) => handleFilterChange('priority', e.target.value)}
          className="filter-select"
        >
          <option value="">{t('allPriorities')}</option>
          <option value="critical">{t('priorityCritical')}</option>
          <option value="high">{t('priorityHigh')}</option>
          <option value="medium">{t('priorityMedium')}</option>
          <option value="low">{t('priorityLow')}</option>
        </select>
      </div>

      {/* Task List */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t('loadingTasks')}</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <p>{t('errorLoadingTasks')} {error}</p>
          <button onClick={refresh} className="btn btn-secondary">{t('retry')}</button>
        </div>
      ) : tasks.length === 0 ? (
        <div className="empty-state">
          <p>{t('noTasksFound')}</p>
          <p className="empty-hint">{t('tryAdjustingFilters')}</p>
        </div>
      ) : (
        <>
          {/* Phase 3: High-Risk Tasks Section */}
          {highRiskTasks.length > 0 && (
            <div className="section-card">
              <h3>
                <AlertTriangle size={20} color="#dc3545" /> {t('highRiskTasksInProgress')} ({highRiskTasks.length})
              </h3>
              <div className="monitoring-table-container">
                <table className="monitoring-table">
                  <thead>
                    <tr>
                      <th>{t('taskName')}</th>
                      <th>{t('executor')}</th>
                      <th>RPN</th>
                      <th>{t('progress')}</th>
                      <th>{t('timeRemaining')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {highRiskTasks.map(exec => {
                      const task = tasks.find(t => t.id === exec.task_id);
                      if (!task) return null;
                      
                      const maxRPN = exec.risk_data?.protocol_max_rpn || 0;
            const progress = getTaskProgress(task);
                      const remaining = exec.estimated_duration ? 
                        Math.max(0, exec.estimated_duration - ((Date.now() - (exec.actual_start?.toMillis?.() || Date.now())) / 1000 / 60))
                        : null;
            
            return (
                        <tr key={exec.id}>
                          <td>
                            <strong>{task.name}</strong>
                            {task.context.roomName && (
                              <div className="task-context">{task.context.roomName}</div>
                            )}
                          </td>
                          <td>{exec.executor_name || 'Unknown'}</td>
                          <td>
                            <span className="rpn-badge rpn-high">{maxRPN}</span>
                          </td>
                          <td>
                            <div className="progress-bar-inline">
                              <div className="progress-fill" style={{ width: `${progress}%` }}></div>
                              <span className="progress-text">{progress}%</span>
                  </div>
                          </td>
                          <td>{remaining !== null ? `${Math.round(remaining)} ${t('minAbbrev')}` : t('nA')}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                  </div>
                </div>
          )}

          {/* Phase 3: Deviations Requiring Action */}
          {criticalDeviations.length > 0 && (
            <div className="section-card">
              <h3>
                <AlertCircle size={20} color="#dc3545" /> {t('deviationsRequiringAction')} ({criticalDeviations.length})
              </h3>
              <div className="deviations-list">
                {criticalDeviations.map((deviation, index) => (
                  <div key={index} className="deviation-item">
                    <div className="deviation-severity">
                      <strong>{deviation.severity?.toUpperCase() || 'MAJOR'}</strong>
                    </div>
                    <div className="deviation-content">
                      <strong>{deviation.deviation_type || deviation.type || t('unknown')}:</strong> {deviation.description}
                    </div>
                  </div>
                ))}
              </div>
                    </div>
                  )}

          {/* All Tasks Table - Using listings instead of cards */}
          <div className="section-card">
            <h3>{t('allTasks')}</h3>
            <div className="monitoring-table-container">
              <table className="monitoring-table">
                <thead>
                  <tr>
                    <th>{t('taskName')}</th>
                    <th>{t('assignedTo')}</th>
                    <th>{t('statusLabel')}</th>
                    <th>{t('priority')}</th>
                    <th>{t('progress')}</th>
                    <th>{t('compliance')}</th>
                    <th>{t('deviations')}</th>
                    <th>{t('scheduledStart')}</th>
                    <th>{t('dueDate')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTasks.map(task => {
                    const progress = getTaskProgress(task);
                    const taskDeviations = task.deviations || [];
                    const maxRPN = task.risk_data?.max_rpn || 0;
                    const ctx = task.context || {};
                    
                    return (
                      <tr
                        key={task.id}
                        className="monitoring-task-row-clickable"
                        onClick={() => setSelectedTask(task)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedTask(task); } }}
                      >
                        <td>
                          <strong>{task.name}</strong>
                          {(ctx.roomName || ctx.departmentName) && (
                            <div className="task-context">
                              {[ctx.departmentName, ctx.roomName].filter(Boolean).join(' → ')}
                            </div>
                          )}
                        </td>
                        <td>{task.assignedToName || task.assignedTo || t('unassigned')}</td>
                        <td>
                          <span className={`status-badge status-${task.status} ${task.completedWithSkips ? 'status-completed-with-skips' : ''}`}>
                            {getTaskStatusLabel(task)}
                          </span>
                        </td>
                        <td>
                          <span className={`priority-badge priority-${task.priority}`}>
                            {task.priority === 'high' ? t('priorityHigh') : task.priority === 'medium' ? t('priorityMedium') : task.priority === 'low' ? t('priorityLow') : task.priority}
                          </span>
                        </td>
                        <td>
                          {task.status === 'in_progress' ? (
                            <div className="progress-bar-inline">
                    <div className="progress-fill" style={{ width: `${progress}%` }}></div>
                    <span className="progress-text">{progress}%</span>
                  </div>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>
                          {task.complianceScore !== null ? (
                            <span className="compliance-score">{task.complianceScore}%</span>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>
                          {taskDeviations.length > 0 ? (
                            <span className="deviations-count">
                              <AlertCircle size={14} /> {taskDeviations.length}
                            </span>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>{formatDateTime(task.scheduledStart)}</td>
                        <td>{formatDateTime(task.scheduledEnd)}</td>
                      </tr>
            );
          })}
                </tbody>
              </table>
            </div>
        </div>

          {/* Task detail modal (row click) */}
          <Modal
            isOpen={!!selectedTask}
            onClose={() => setSelectedTask(null)}
            title={selectedTask ? selectedTask.name : ''}
            size="large"
          >
            {selectedTask && (
              <div className="task-detail-modal-content">
                <section className="task-detail-section">
                  <h4><MapPin size={16} /> {t('location')}</h4>
                  <p className="task-detail-location">
                    {(() => {
                      const c = selectedTask.context || {};
                      const parts = [c.departmentName || c.departmentId, c.roomName || c.roomId].filter(Boolean);
                      return parts.length ? parts.join(' → ') : '—';
                    })()}
                  </p>
                </section>

                <section className="task-detail-section">
                  <h4><Shield size={16} /> {t('riskAssessment')}</h4>
                  {selectedTask.risk_data || selectedTask.baseRPN != null ? (
                    <>
                      {(() => {
                        const rpn = selectedTask.risk_data?.max_rpn ?? selectedTask.baseRPN ?? selectedTask.currentRPN ?? 0;
                        const level = selectedTask.riskLevel || (rpn ? riskCalculationService.getRiskLevel(rpn)?.level : null);
                        return (
                          <p className="task-detail-risk-summary">
                            <span className={`task-detail-risk-level risk-level-${(level || 'unknown').toLowerCase().replace(/\s+/g, '-')}`}>
                              {level || '—'}
                            </span>
                            {rpn > 0 && (
                              <> — Max RPN: <strong>{selectedTask.risk_data?.max_rpn ?? selectedTask.baseRPN ?? rpn}</strong></>
                            )}
                        </p>
                        );
                      })()}
                      {(selectedTask.risk_data?.avg_rpn != null || selectedTask.risk_data?.risk_count != null) && (
                        <p className="task-detail-meta">
                          {selectedTask.risk_data.avg_rpn != null && <>{t('avgRpn')}: {selectedTask.risk_data.avg_rpn}</>}
                          {selectedTask.risk_data.risk_count != null && (
                            <> &nbsp;·&nbsp; {selectedTask.risk_data.risk_count} {t('risksInScope')}</>
                          )}
                        </p>
                      )}
                      {selectedTask.risk_data?.critical_risks?.length > 0 && (
                        <ul className="task-detail-risks-list">
                          {selectedTask.risk_data.critical_risks.map((r, i) => (
                            <li key={r.id || i}>
                              <span className="risk-rpn-badge">RPN {r.rpn}</span>
                              {r.scenario || r.riskScenario || t('criticalRisk')}
                              {r.criticality && <span className="risk-criticality"> ({r.criticality})</span>}
                            </li>
                          ))}
                        </ul>
                      )}
                      {selectedTask.risk_data?.biological_entities?.length > 0 && (
                        <p className="task-detail-meta">
                          {t('biologicalEntitiesLabel')}: {selectedTask.risk_data.biological_entities.map(e => e.name || e.id).join(', ')}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="task-detail-muted">{t('noRiskAssessment')}</p>
                  )}
                </section>

                <section className="task-detail-section">
                  <h4>{t('progress')}</h4>
                  {(() => {
                    const { completedSteps, totalSteps } = getTaskProgressBreakdown(selectedTask);
                    const pct = totalSteps ? Math.round((completedSteps / totalSteps) * 100) : 0;
                    return (
                      <p>
                        <strong>{pct}%</strong> — {t('stepsCompletedOf', { completed: completedSteps, total: totalSteps })}
                        {selectedTask.protocols?.length > 0 && (
                          <span className="task-detail-meta"> ({t('protocolsCount', { count: selectedTask.protocols.length })})</span>
                        )}
                      </p>
                    );
                  })()}
                </section>

                <section className="task-detail-section">
                  <h4>{t('compliance')}</h4>
                  {selectedTask.complianceScore !== null && selectedTask.complianceScore !== undefined ? (
                    <>
                      <p>
                        <strong>{selectedTask.complianceScore}%</strong> overall
                        {selectedTask.completedWithSkips && (
                          <span className="task-detail-muted"> {t('withSkipsErrors')}</span>
                        )}
                      </p>
                      {selectedTask.protocols?.some(p => p.complianceScore != null) && (
                        <ul className="task-detail-compliance-list">
                          {selectedTask.protocols.filter(p => p.complianceScore != null).map((p, i) => (
                            <li key={i}>
                              {p.protocolName || `${t('protocol')} ${i + 1}`}: {p.complianceScore}%
                              {(p.status === 'completed_with_skips' || p.completedWithSkips) && (
                                <span className="task-detail-muted"> {t('withSkipsErrors')}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  ) : (
                    <p className="task-detail-muted">{t('notYetCalculated')}</p>
                  )}
                </section>

                <section className="task-detail-section">
                  <h4>{t('skipsAndErrors')}</h4>
                  {(() => {
                    const skips = [];
                    const errors = [];
                    (selectedTask.protocols || []).forEach((p, pi) => {
                      (p.steps || []).forEach((s, si) => {
                        if (s.isSkipped) {
                          skips.push({
                            protocol: p.protocolName || `Protocol ${pi + 1}`,
                            step: s.stepName || `Step ${si + 1}`,
                            reason: s.skipReason
                          });
                        } else if (s.completedWithErrors) {
                          const incompleteNames = s.incompleteActionNames || (s.actions || []).filter(a => !a.completed).map(a => a.actionName || a.name || 'Action').filter(Boolean);
                          errors.push({
                            protocol: p.protocolName || `Protocol ${pi + 1}`,
                            step: s.stepName || `Step ${si + 1}`,
                            incompleteActionNames: incompleteNames
                          });
                        }
                      });
                    });
                    return (
                      <div className="task-detail-skips-errors">
                        <div className="skips-errors-subsection">
                          <strong>{t('skippedSteps')}</strong>
                          {skips.length === 0 ? (
                            <p className="task-detail-muted">{t('none')}</p>
                          ) : (
                            <ul className="task-detail-skips-list">
                              {skips.map((item, i) => (
                                <li key={`s-${i}`} className="task-detail-skip">
                                  <span className="skip-step-title"><span className="skip-context">{item.protocol}</span> — <strong className="skip-step-name">{item.step}</strong></span>
                                  {item.reason && <p className="skip-reason-detail"><strong>{t('reasonLabel')}: {item.reason}</strong></p>}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        <div className="skips-errors-subsection">
                          <strong>{t('stepsWithErrors')}</strong>
                          {errors.length === 0 ? (
                            <p className="task-detail-muted">{t('none')}</p>
                          ) : (
                            <ul className="task-detail-skips-list">
                              {errors.map((item, i) => (
                                <li key={`e-${i}`} className="task-detail-error">
                                  <span className="error-step-title"><span className="error-context">{item.protocol}</span> — <strong className="error-step-name">{item.step}</strong></span>
                                  {item.incompleteActionNames && item.incompleteActionNames.length > 0 ? (
                                    <p className="incomplete-actions-detail"><strong>{t('actionsNotCompleted')}: {item.incompleteActionNames.join(', ')}</strong></p>
                                  ) : (
                                    <p className="incomplete-actions-detail"><strong>{t('someActionsNotMarkedComplete')}</strong></p>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </section>

                <section className="task-detail-section">
                  <h4><AlertCircle size={16} /> {t('deviations')}</h4>
                  {(selectedTask.deviations && selectedTask.deviations.length > 0) ? (
                    <ul className="task-detail-deviations-list">
                      {selectedTask.deviations.map((d, i) => (
                        <li key={i} className={`task-detail-deviation severity-${d.severity || 'minor'}`}>
                          <span className="deviation-severity">{d.severity || 'minor'}</span>
                          <span className="deviation-type">{d.deviation_type || d.type || t('deviations')}</span>
                          <p className="deviation-description">{d.description || '—'}</p>
                          {d.corrective_action && <p className="deviation-corrective">{t('corrective')}: {d.corrective_action}</p>}
                          {d.resolved && <span className="deviation-resolved">{t('resolved')}</span>}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="task-detail-muted">{t('noDeviationsRecorded')}</p>
                  )}
                </section>

                <div className="task-detail-meta-row">
                  <span>{t('assignedToLabel')}: {selectedTask.assignedToName || selectedTask.assignedTo || t('unassigned')}</span>
                  <span>{t('statusLabel')}: {getTaskStatusLabel(selectedTask)}</span>
                  <span>{t('dueLabel')}: {formatDateTime(selectedTask.scheduledEnd)}</span>
                </div>
              </div>
            )}
          </Modal>
        </>
      )}
    </div>
  );
};

export default TaskMonitoringDashboard;

